import type { TimelineEventTypes } from '@ViewModels';
import { StyleDeclarationValue } from 'aphrodite';
import { useState } from 'react';
import {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownSelectTrigger,
	DropdownSeparator,
} from '../../../../components-v2/Dropdown';
import { styleSheet } from './styles';

export interface ITimelineFilterOption {
	id: string;
	/** Lower-case fragment used to build the trigger summary, e.g. `notes` renders as "Show notes". */
	name: string;
	label: string;
	types: TimelineEventTypes[];
}

export interface ITimelineFilterProps {
	options: ITimelineFilterOption[];
	selectedIds: string[];
	/** Fired once the menu closes, mirroring the committed-on-dismiss behavior of the filter it replaced. */
	onCommit: (selectedIds: string[]) => void;
	styles?: StyleDeclarationValue[];
}

const MAX_TRIGGER_LABEL_LENGTH = 25;

export function getTimelineFilterTriggerLabel(options: ITimelineFilterOption[], selectedIds: string[]): string {
	if (options.length > 0 && options.every(option => selectedIds.includes(option.id))) {
		return 'Show all';
	}
	const names = options.filter(option => selectedIds.includes(option.id)).map(option => option.name);
	const text = `Show ${names.join(' and ') || 'none'}`;
	return text.length > MAX_TRIGGER_LABEL_LENGTH ? `${text.slice(0, MAX_TRIGGER_LABEL_LENGTH)}...` : text;
}

export function TimelineFilter({ options, selectedIds, onCommit, styles = [] }: ITimelineFilterProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [pendingIds, setPendingIds] = useState<string[]>(selectedIds);

	// While closed the committed props are the truth; the pending set only drives the menu it belongs to.
	const displayedIds = isOpen ? pendingIds : selectedIds;
	const allSelected = options.length > 0 && options.every(option => pendingIds.includes(option.id));

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (open) {
			setPendingIds(selectedIds);
			return;
		}
		// An empty type list sends no constraint to the server, which then returns *every* event — the
		// opposite of what an empty filter implies. So an empty selection on dismiss commits as "all".
		onCommit(pendingIds.length > 0 ? pendingIds : options.map(option => option.id));
	};

	const toggleOption = (id: string) => {
		setPendingIds(current => (current.includes(id) ? current.filter(x => x !== id) : [...current, id]));
	};

	const toggleAll = () => {
		setPendingIds(allSelected ? [] : options.map(option => option.id));
	};

	return (
		<Dropdown onOpenChange={handleOpenChange}>
			<DropdownSelectTrigger styles={[styleSheet.trigger, ...styles]} aria-label='Filter timeline by type'>
				{getTimelineFilterTriggerLabel(options, displayedIds)}
			</DropdownSelectTrigger>
			<DropdownContent align='end'>
				<DropdownCheckboxItem checked={allSelected} onCheckedChange={toggleAll}>
					Show all
				</DropdownCheckboxItem>
				<DropdownSeparator />
				{options.map(option => (
					<DropdownCheckboxItem
						key={option.id}
						checked={pendingIds.includes(option.id)}
						onCheckedChange={() => toggleOption(option.id)}
					>
						{option.label}
					</DropdownCheckboxItem>
				))}
			</DropdownContent>
		</Dropdown>
	);
}
